import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewChecked, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { XWareLogService } from '../../services/xware-log.service';
import { User } from '../../models/user.model';
import { IAuthService, IAUTHSERVICE } from '../../services/iauth.service';
import { IDIRECTORYSERVICE, IDirectoryService } from '../../services/idirectory.service';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'xware-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
})
export class UserMenuComponent implements OnInit, AfterViewChecked, OnDestroy {
  constructor(
    @Inject(IDIRECTORYSERVICE) private directoryService: IDirectoryService,
    private sanitizer: DomSanitizer,
    private router: Router,
    public loginService: LoginService,
    private logger: XWareLogService,
    @Inject(IAUTHSERVICE) public authService: IAuthService,
    @Inject('appVersion') public appVersion: string
  ) {}

  userName: String;
  fullName = '';
  displayNameReady = false;
  user = new User();
  public loginFailed: boolean;
  showTenants = false;
  picLoaded = false;
  showAvatar = false;
  currentAccess: string = '';

  @ViewChild(MatMenuTrigger, { static: false }) menuTrigger: MatMenuTrigger;

  playerSrc: SafeResourceUrl | String = '';
  ADType = '';
  private destroy$ = new Subject<void>();
  private profileObjectUrl = '';
  private usingLetterAvatar = false;

  ngOnInit() {
    this.ADType = sessionStorage.getItem('adType');
    this.userName = sessionStorage.getItem('userPrincipalName');

    if (this.ADType !== 'gapi') {
      this.userName = this.authService.authuser()?.username || this.userName;
    }
    this.loadProfilePic();

    this.loginService.totaluserLatest.pipe(takeUntil(this.destroy$)).subscribe((user: User) => {
      if (user) {
        if (!this.userName && user.upn) {
          this.userName = user.upn;
        }
        const g = (user.givenName || '').trim();
        const s = (user.surname || '').trim();
        this.fullName = g && s ? `${g} ${s}` : (g || s || this.userName || '').toString();
        if (!this.fullName && this.userName) {
          this.fullName = this.userName.toString();
        }
        this.displayNameReady = true;
      }
    });

    this.getTenants();
    this.calcCurrentAccess();
  }

  ngAfterViewChecked(): void {
    const el2 = document.getElementById('divSwitchButton');
    if (el2?.innerHTML == '') {
      this.authService.renderButtonSwitch('divSwitchButton');
    }
  }

  calcCurrentAccess() {
    this.loginService.portaluserLatest.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        if (result.isGlobalAdmin) {
          this.currentAccess = 'Global Admin Access';
        } else {
          if (result.isPortalAdmin) {
            this.currentAccess = 'Portal Admin Access';
          } else {
            if (result.dBVersion === 2) {
              if (result.isAdminUnitAdmin) {
                this.currentAccess = 'Admin User Access';
              } else {
                this.currentAccess = 'User Access';
              }
            } else {
              this.currentAccess = 'User Access';
            }
          }
        }
      }
    });
  }

  public logout() {
    this.authService.logout();
    this.logger.log('user-menu:logout called ');
    this.router.navigateByUrl(
      this.router.createUrlTree(['/login'], { queryParams: { logout: 'y' } })
    );
  }

  public LetterAvatar(name, size) {
    name = name || '';

    let colours = [
        '#1abc9c',
        '#2ecc71',
        '#3498db',
        '#9b59b6',
        '#34495e',
        '#16a085',
        '#27ae60',
        '#2980b9',
        '#8e44ad',
        '#2c3e50',
        '#f1c40f',
        '#e67e22',
        '#e74c3c',
        '#ecf0f1',
        '#95a5a6',
        '#f39c12',
        '#d35400',
        '#c0392b',
        '#bdc3c7',
        '#7f8c8d',
      ],
      nameSplit = String(name).toUpperCase().split(' '),
      initials,
      charIndex,
      colourIndex,
      canvas,
      context,
      dataURI;

    if (nameSplit.length == 1) {
      initials = nameSplit[0] ? nameSplit[0].charAt(0) : '?';
    } else {
      initials = nameSplit[0].charAt(0) + nameSplit[1].charAt(0);
    }

    charIndex = (initials == '?' ? 72 : initials.charCodeAt(0)) - 64;
    colourIndex = charIndex % 20;
    canvas = <HTMLCanvasElement>document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    context = canvas.getContext('2d');

    context.fillStyle = colours[colourIndex - 1];
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.font = Math.round(canvas.width / 2) + 'px Arial';
    context.textAlign = 'center';
    context.fillStyle = '#FFF';
    context.fillText(initials, size / 2, size / 1.5);

    dataURI = canvas.toDataURL();
    canvas = null;

    this.picLoaded = true;
    this.usingLetterAvatar = true;

    return dataURI;
  }

  onProfileImageError(): void {
    if (this.usingLetterAvatar) {
      return;
    }
    this.applyLetterAvatarFallback();
  }

  private loadProfilePic(): void {
    if (this.userName) {
      this.applyLetterAvatarFallback();
    }

    if (this.getPhotoUserKey()) {
      this.fetchProfilePic();
      return;
    }

    this.loginService.totaluserLatest
      .pipe(
        takeUntil(this.destroy$),
        filter((user): user is User => !!user),
        take(1)
      )
      .subscribe(() => this.fetchProfilePic());
  }

  private fetchProfilePic(): void {
    this.directoryService
      .getProfilePic()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (this.applyProfileBlob(response)) {
            this.showAvatar = true;
            return;
          }
          this.playerSrc = '';
          this.applyLetterAvatarFallback();
        },
        error: (err: HttpErrorResponse) => {
          this.showAvatar = true;
          this.logger.log('user-menu:' + JSON.stringify(err?.message || err));
          this.applyLetterAvatarFallback();
        },
        complete: () => {
          this.showAvatar = true;
        },
      });
  }

  private getPhotoUserKey(): string {
    return sessionStorage.getItem('user_id') || sessionStorage.getItem('userPrincipalName') || '';
  }

  private isValidImageBlob(blob: Blob | null | undefined): boolean {
    if (!blob || blob.size === 0) {
      return false;
    }
    const type = (blob.type || '').toLowerCase();
    return type.startsWith('image/') || type === 'application/octet-stream' || type === '';
  }

  private applyProfileBlob(response: Blob | Blob[] | null): boolean {
    if (response == null) {
      return false;
    }

    try {
      const blob = Array.isArray(response) ? response[0] : response;
      if (!this.isValidImageBlob(blob)) {
        return false;
      }

      this.directoryService.profilePic.next([blob]);
      this.revokeProfileObjectUrl();
      this.profileObjectUrl = URL.createObjectURL(blob);
      this.playerSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.profileObjectUrl);
      this.picLoaded = true;
      this.usingLetterAvatar = false;
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      this.logger.log('user-menu:Error getting profile picture: ' + message);
      return false;
    }
  }

  private applyLetterAvatarFallback(): void {
    if (this.usingLetterAvatar && this.picLoaded) {
      return;
    }
    if (!this.userName) {
      this.userName =
        sessionStorage.getItem('userPrincipalName') || this.authService.authuser()?.username || '';
    }
    this.logger.log('user Name: ' + this.userName);
    this.revokeProfileObjectUrl();
    this.playerSrc = this.LetterAvatar(this.userName, 40);
  }

  private revokeProfileObjectUrl(): void {
    if (this.profileObjectUrl) {
      URL.revokeObjectURL(this.profileObjectUrl);
      this.profileObjectUrl = '';
    }
  }

  private getTenants() {
    this.loginService.tenants.pipe(takeUntil(this.destroy$)).subscribe(t => {
      if (t) {
        if (t.length > 1) {
          this.showTenants = true;
        }
      }
    });
  }

  openHelp(): void {
    window.open('https://kb.crossware365.com', '_blank', 'noopener');
    this.menuTrigger.closeMenu();
  }

  ngOnDestroy(): void {
    this.revokeProfileObjectUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
